/**
 * <insight-slider> — vertical card carousel for the Insight Moment section.
 *
 * Replaces slick's vertical mode, which re-measured heights, snapped on the
 * infinite-loop wrap and captured vertical touch gestures. The whole stack
 * moves with a single composited transform on the track (`--insight-index`
 * drives it from CSS); two clones at each end give the peek-above/below
 * look and let the loop wrap invisibly once a transition has finished.
 *
 * Markup (inside .insight_media):
 *   <insight-slider data-autoplay="3000">
 *     <div class="insight_card_viewport"><div class="insight_card_track">…cards…</div></div>
 *     <div class="insight_nav"><button data-dir="-1"/><button data-dir="1"/></div>
 *   </insight-slider>
 *
 * Autoplay pauses on hover, focus, while off-screen or in a hidden tab, and
 * is skipped entirely for `prefers-reduced-motion`. Arrow keys work while the
 * viewport is focused; a mouse/pen drag of 40px+ also steps.
 */
class InsightSlider extends HTMLElement {
  static CLONES = 2;

  connectedCallback() {
    if (this.track) return;
    this.viewport = this.querySelector('.insight_card_viewport');
    this.track = this.querySelector('.insight_card_track');
    if (!this.viewport || !this.track) return;

    const originals = Array.from(this.track.children).filter((el) => el.classList.contains('insight_card'));
    this.count = originals.length;
    if (this.count < 2) {
      originals[0]?.classList.add('is-current');
      this.querySelector('.insight_nav')?.remove();
      return;
    }

    const clone = (el) => {
      const copy = el.cloneNode(true);
      copy.classList.add('is-clone');
      copy.setAttribute('aria-hidden', 'true');
      copy.removeAttribute('data-shopify-editor-block');
      copy.removeAttribute('data-block-id');
      copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      return copy;
    };
    originals
      .slice(-InsightSlider.CLONES)
      .map(clone)
      .forEach((copy) => this.track.insertBefore(copy, this.track.firstChild));
    originals
      .slice(0, InsightSlider.CLONES)
      .map(clone)
      .forEach((copy) => this.track.appendChild(copy));

    this.items = Array.from(this.track.children);
    this.index = InsightSlider.CLONES;
    this.animating = false;
    this.hovered = false;
    this.visible = true;
    this.delay = parseInt(this.dataset.autoplay, 10) || 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.#size();
    this.#render(false);

    this.resizeObserver = new ResizeObserver(() => this.#size());
    this.resizeObserver.observe(this.viewport);

    this.querySelectorAll('[data-dir]').forEach((button) => {
      button.addEventListener('click', () => {
        this.#step(parseInt(button.dataset.dir, 10) || 1);
        this.#schedule();
      });
    });

    this.viewport.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      this.#step(event.key === 'ArrowDown' ? 1 : -1);
      this.#schedule();
    });

    this.track.addEventListener('transitionend', (event) => {
      if (event.target === this.track && event.propertyName === 'transform') this.#settle();
    });

    // Mouse/pen drag. Touch is left to the page so it can still scroll.
    let dragStart = null;
    this.viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      dragStart = event.clientY;
    });
    const endDrag = (event) => {
      if (dragStart === null) return;
      const delta = event.clientY - dragStart;
      dragStart = null;
      if (Math.abs(delta) < 40) return;
      this.#step(delta < 0 ? 1 : -1);
      this.#schedule();
    };
    this.viewport.addEventListener('pointerup', endDrag);
    this.viewport.addEventListener('pointercancel', () => (dragStart = null));

    // Autoplay gates
    const host = this.parentElement || this;
    this.onEnter = () => {
      this.hovered = true;
      this.#schedule();
    };
    this.onLeave = () => {
      this.hovered = false;
      this.#schedule();
    };
    host.addEventListener('pointerenter', this.onEnter);
    host.addEventListener('pointerleave', this.onLeave);
    this.addEventListener('focusin', this.onEnter);
    this.addEventListener('focusout', this.onLeave);

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.#schedule();
    });
    this.intersectionObserver.observe(this.viewport);

    this.onVisibility = () => this.#schedule();
    document.addEventListener('visibilitychange', this.onVisibility);

    // Theme editor: bring a selected card into view and hold it there.
    this.onBlockSelect = (event) => {
      const card = event.target?.closest?.('.insight_card');
      if (!card || !this.track.contains(card)) return;
      const position = this.items.indexOf(card);
      if (position < 0) return;
      this.selected = true;
      this.index = position;
      this.#render(true);
      this.#schedule();
    };
    this.onBlockDeselect = () => {
      this.selected = false;
      this.#schedule();
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.#schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    clearTimeout(this.settleTimer);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    const host = this.parentElement || this;
    host.removeEventListener('pointerenter', this.onEnter);
    host.removeEventListener('pointerleave', this.onLeave);
  }

  /**
   * The focused card is half the viewport; neighbours peek a quarter each.
   * Rounded to an even number so the track's resting offset (half a card plus
   * whole cards and gaps) lands on a whole pixel and the text stays crisp.
   */
  #size() {
    const height = this.viewport.clientHeight;
    if (!height) return;
    this.style.setProperty('--insight-card-height', `${Math.round(height / 4) * 2}px`);
  }

  #render(animate) {
    this.track.classList.toggle('is-jumping', !animate);
    this.track.style.setProperty('--insight-index', this.index);
    this.items.forEach((item, position) => item.classList.toggle('is-current', position === this.index));
    if (!animate) {
      void this.track.offsetHeight; // commit the un-animated position before transitions come back
      this.track.classList.remove('is-jumping');
    }
  }

  #step(direction) {
    if (this.animating) return;
    this.animating = true;
    this.index += direction;
    this.#render(!this.reducedMotion.matches);
    clearTimeout(this.settleTimer);
    // transitionend can be skipped (reduced motion, hidden tab); settle regardless.
    this.settleTimer = setTimeout(() => this.#settle(), 900);
  }

  /** After a move lands on a clone, jump silently to the matching real card. */
  #settle() {
    clearTimeout(this.settleTimer);
    this.animating = false;
    const clones = InsightSlider.CLONES;
    if (this.index >= this.count + clones) {
      this.index -= this.count;
      this.#render(false);
    } else if (this.index < clones) {
      this.index += this.count;
      this.#render(false);
    }
  }

  #schedule() {
    clearTimeout(this.timer);
    const blocked =
      !this.delay || this.reducedMotion.matches || this.hovered || this.selected || !this.visible || document.hidden;
    if (blocked) return;
    this.timer = setTimeout(() => {
      this.#step(1);
      this.#schedule();
    }, this.delay);
  }
}

if (!customElements.get('insight-slider')) customElements.define('insight-slider', InsightSlider);
